/*------------------------------------------------------------------------------
   About      : Central registry of GA4 analytics event names for DETO.

                Naming convention : page_[feature]_[action]
                Source of truth    : Confluence "Google Analytics Integration
                                     in DETO" (event taxonomy).

                Add new event names here as they are instrumented. The
                implementation tracker lives at:
                  docs/analytics/GA4_EVENT_TRACKING.md

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/

export const ANALYTICS_EVENTS = {

  // ── Generic UI (shared components such as common buttons) ──
  UI : {
    BUTTON_CLICKED : 'page_ui_button_clicked',
  },

  AUTH : {
    LOGIN_LOADED        : 'page_auth_login_loaded',
    LOGIN_EMAIL_FOCUSED : 'page_auth_login_email_focused',
    LOGIN_SUBMITTED     : 'page_auth_login_submitted',
    LOGIN_SUCCESS       : 'page_auth_login_success',
    LOGIN_FAILED        : 'page_auth_login_failed',
    LOGOUT_CLICKED      : 'page_auth_logout_clicked',
    LOGOUT_COMPLETE     : 'page_auth_logout_complete',

    FORGOT_PASSWORD_OPENED    : 'page_auth_forgot_password_opened',
    FORGOT_PASSWORD_SUBMITTED : 'page_auth_forgot_password_submitted',
    FORGOT_PASSWORD_SUCCESS   : 'page_auth_forgot_password_success',
    FORGOT_PASSWORD_FAILED    : 'page_auth_forgot_password_failed',

    RESET_PASSWORD_OPENED     : 'page_auth_reset_password_opened',
    RESET_PASSWORD_SUBMITTED  : 'page_auth_reset_password_submitted',
    RESET_PASSWORD_SUCCESS    : 'page_auth_reset_password_success',
    RESET_PASSWORD_FAILED     : 'page_auth_reset_password_failed',
  },

  RECOMMENDATIONS : {
    HOME_LOADED                 : 'page_recommendations_home_loaded',
    HOME_FILTER_APPLIED         : 'page_recommendations_home_filter_applied',
    HOME_SORT_CHANGED           : 'page_recommendations_home_sort_changed',
    HOME_RECOMMENDATION_CLICKED : 'page_recommendations_home_recommendation_clicked',
    EDITOR_LOADED               : 'page_recommendations_editor_loaded',
    EDITOR_TITLE_CHANGED        : 'page_recommendations_editor_title_changed',
    EDITOR_DESCRIPTION_CHANGED  : 'page_recommendations_editor_description_changed',
    EDITOR_SAVE_CLICKED         : 'page_recommendations_editor_save_clicked',
    EDITOR_SAVED                : 'page_recommendations_editor_saved',
    EDITOR_SAVE_FAILED          : 'page_recommendations_editor_save_failed',
    EDITOR_CANCEL_CLICKED       : 'page_recommendations_editor_cancel_clicked',

    UPLOAD_INITIATED              : 'page_recommendations_upload_initiated',
    UPLOAD_FILE_SELECTED          : 'page_recommendations_upload_file_selected',
    UPLOAD_FILE_VALIDATED         : 'page_recommendations_upload_file_validated',
    UPLOAD_FILE_VALIDATION_FAILED : 'page_recommendations_upload_file_validation_failed',
    UPLOAD_PROCESSING             : 'page_recommendations_upload_processing',
    UPLOAD_COMPLETED              : 'page_recommendations_upload_completed',
    UPLOAD_FAILED                 : 'page_recommendations_upload_failed',

    COMMENT_BOX_FOCUSED         : 'page_recommendations_comment_box_focused',
    COMMENT_SUBMITTED           : 'page_recommendations_comment_submitted',
  },

  // ── Content Management (CX Template Editor / Color Palette / Data Scenarios) ──
  CONTENT : {
    TEMPLATE_EDITOR_OPENED        : 'page_Template_Editor_editor_opened',
    TEMPLATE_TEXT_EDITED          : 'page_Template_Editor_text_edited',
    TEMPLATE_IMAGE_ADDED          : 'page_Template_Editor_image_added',
    TEMPLATE_IMAGE_REMOVED        : 'page_Template_Editor_image_removed',
    TEMPLATE_LINK_INSERTED        : 'page_Template_Editor_link_inserted',
    TEMPLATE_LINK_REMOVED         : 'page_Template_Editor_link_removed',
    TEMPLATE_COMPONENT_ADDED      : 'page_Template_Editor_component_added',
    TEMPLATE_COMPONENT_REMOVED    : 'page_Template_Editor_component_removed',
    TEMPLATE_COMPONENT_CONFIGURED : 'page_Template_Editor_component_configured',
    TEMPLATE_FORMATTING_APPLIED   : 'page_Template_Editor_formatting_applied',
    TEMPLATE_UNDO_CLICKED         : 'page_Template_Editor_undo_clicked',
    TEMPLATE_REDO_CLICKED         : 'page_Template_Editor_redo_clicked',
    TEMPLATE_TEMPLATE_SELECTED    : 'page_Template_Editor_template_selected',
    TEMPLATE_TEMPLATE_APPLIED     : 'page_Template_Editor_template_applied',
    TEMPLATE_SAVE_CLICKED         : 'page_Template_Editor_save_clicked',
    TEMPLATE_SAVED                : 'page_Template_Editor_saved',
    TEMPLATE_SAVE_FAILED          : 'page_Template_Editor_save_failed',

    COLOR_PALETTE_OPENED          : 'page_content_color_palette_opened',
    COLOR_ADDED                   : 'page_content_color_added',
    COLOR_REMOVED                 : 'page_content_color_removed',
    COLOR_RENAMED                 : 'page_content_color_renamed',
    COLOR_APPLIED                 : 'page_content_color_applied',
    STYLE_PRESET_CREATED          : 'page_content_style_preset_created',
    STYLE_PRESET_APPLIED          : 'page_content_style_preset_applied',

    SCENARIOS_LIST_OPENED         : 'page_content_scenarios_list_opened',
    SCENARIO_CREATED              : 'page_content_scenario_created',
    SCENARIO_EDITED               : 'page_content_scenario_edited',
    SCENARIO_DELETED              : 'page_content_scenario_deleted',
    SCENARIO_TESTED               : 'page_content_scenario_tested',
    VARIABLE_INSERTED             : 'page_content_variable_inserted',
    VARIABLE_MAPPING_CHANGED      : 'page_content_variable_mapping_changed',
  },

  // ── Config Registry ──
  CONFIG_REGISTRY : {
    LOADED                 : 'page_config_registry_loaded',
    SEARCH_EXECUTED        : 'page_config_registry_search_executed',
    FILTER_APPLIED         : 'page_config_registry_filter_applied',
    SORT_CHANGED           : 'page_config_registry_sort_changed',
    CONFIG_CLICKED         : 'page_config_registry_config_clicked',
    CREATE_DIALOG_OPENED   : 'page_config_registry_create_dialog_opened',
    CONFIG_NAME_ENTERED    : 'page_config_registry_config_name_entered',
    CONFIG_TYPE_SELECTED   : 'page_config_registry_config_type_selected',
    CONFIG_CREATED         : 'page_config_registry_config_created',
    EDITOR_OPENED          : 'page_config_registry_editor_opened',
    FIELD_CHANGED          : 'page_config_registry_field_changed',
    VALIDATION_TRIGGERED   : 'page_config_registry_validation_triggered',
    VALIDATION_PASSED      : 'page_config_registry_validation_passed',
    VALIDATION_FAILED      : 'page_config_registry_validation_failed',
    DRAFT_SAVED            : 'page_config_registry_draft_saved',
    PUBLISH_CLICKED        : 'page_config_registry_publish_clicked',
    PUBLISHED              : 'page_config_registry_published',
    PUBLISH_FAILED         : 'page_config_registry_publish_failed',
    DELETE_CLICKED         : 'page_config_registry_delete_clicked',
    DELETE_CONFIRMED       : 'page_config_registry_delete_confirmed',
    DELETED                : 'page_config_registry_deleted',
    HISTORY_OPENED         : 'page_config_registry_history_opened',
    VERSION_SELECTED       : 'page_config_registry_version_selected',
    VERSION_PREVIEW_OPENED : 'page_config_registry_version_preview_opened',
    VERSION_REVERTED       : 'page_config_registry_version_reverted',
  },

  // ── Config Tools (DCX Clusters / Feature Access / Template Personalization / Compare / Promotion) ──
  CONFIG_TOOLS : {
    DCX_CLUSTERS_LOADED            : 'page_dcx_clusters_loaded',
    DCX_CLUSTER_CREATED            : 'page_dcx_cluster_created',
    DCX_CLUSTER_EDITED             : 'page_dcx_cluster_edited',
    DCX_CLUSTER_DELETED            : 'page_dcx_cluster_deleted',
    DCX_CLUSTER_MEMBERS_ADDED      : 'page_dcx_cluster_members_added',
    DCX_CLUSTER_MEMBERS_REMOVED    : 'page_dcx_cluster_members_removed',
    DCX_CLUSTER_SAVED              : 'page_dcx_cluster_saved',

    FEATURE_ACCESS_LOADED          : 'page_feature_access_loaded',
    FEATURE_ACCESS_ROLE_SELECTED   : 'page_feature_access_role_selected',
    FEATURE_ACCESS_FEATURE_TOGGLED : 'page_feature_access_feature_toggled',
    FEATURE_ACCESS_TIER_CHANGED    : 'page_feature_access_tier_changed',
    FEATURE_ACCESS_SAVED           : 'page_feature_access_saved',
    FEATURE_ACCESS_RESET_CLICKED   : 'page_feature_access_reset_clicked',

    TEMPLATE_PERSONALIZATION_LOADED : 'page_template_personalization_loaded',
    TEMPLATE_RULE_CREATED          : 'page_template_rule_created',
    TEMPLATE_RULE_EDITED           : 'page_template_rule_edited',
    TEMPLATE_RULE_DELETED          : 'page_template_rule_deleted',
    TEMPLATE_RULE_TESTED           : 'page_template_rule_tested',
    TEMPLATE_TEST_PASSED           : 'page_template_test_passed',
    TEMPLATE_TEST_FAILED           : 'page_template_test_failed',
    TEMPLATE_RULES_SAVED           : 'page_template_rules_saved',

    CONFIG_COMPARE_OPENED          : 'page_config_compare_opened',
    CONFIG_COMPARE_VERSION_SELECTED : 'page_config_compare_version_selected',
    CONFIG_COMPARE_DIFFERENCE_HIGHLIGHTED : 'page_config_compare_difference_highlighted',
    CONFIG_COMPARE_MERGE_REQUESTED : 'page_config_compare_merge_requested',
    CONFIG_COMPARE_MERGE_EXECUTED  : 'page_config_compare_merge_executed',
    CONFIG_COMPARE_MERGE_CONFLICT_DETECTED : 'page_config_compare_merge_conflict_detected',
    CONFIG_COMPARE_CONFLICT_RESOLVED : 'page_config_compare_conflict_resolved',

    CONFIG_PROMOTION_INITIATED     : 'page_config_promotion_initiated',
    CONFIG_PROMOTION_REVIEW_OPENED : 'page_config_promotion_review_opened',
    CONFIG_PROMOTION_APPROVED      : 'page_config_promotion_approved',
    CONFIG_PROMOTION_REJECTED      : 'page_config_promotion_rejected',
    CONFIG_PROMOTION_EXECUTED      : 'page_config_promotion_executed',
    CONFIG_PROMOTION_FAILED        : 'page_config_promotion_failed',
    CONFIG_PROMOTION_ROLLBACK_INITIATED : 'page_config_promotion_rollback_initiated',
    CONFIG_PROMOTION_ROLLBACK_COMPLETED : 'page_config_promotion_rollback_completed',
  },

  // ── Project Management (list / create / details / app scope / configs) ──
  PROJECTS : {
    HOME_LOADED                 : 'page_projects_home_loaded',
    FILTER_APPLIED              : 'page_projects_filter_applied',
    SORT_CHANGED                : 'page_projects_sort_changed',
    SEARCH_EXECUTED             : 'page_projects_search_executed',
    PROJECT_CLICKED             : 'page_projects_project_clicked',
    CREATE_DIALOG_OPENED        : 'page_projects_create_dialog_opened',
    CREATE_PROJECT_NAME_ENTERED : 'page_projects_create_project_name_entered',
    CREATE_UTILITY_SELECTED     : 'page_projects_create_utility_selected',
    CREATE_PROJECT_CREATED      : 'page_projects_create_project_created',
    CREATE_FAILED               : 'page_projects_create_failed',
    DETAILS_LOADED              : 'page_projects_details_loaded',
    DETAILS_TITLE_EDITED        : 'page_projects_details_title_edited',
    DETAILS_DESCRIPTION_EDITED  : 'page_projects_details_description_edited',
    DETAILS_OWNER_CHANGED       : 'page_projects_details_owner_changed',
    DETAILS_STATUS_CHANGED      : 'page_projects_details_status_changed',
    DETAILS_SAVED               : 'page_projects_details_saved',
    DETAILS_ARCHIVE_CLICKED     : 'page_projects_details_archive_clicked',
    DETAILS_ARCHIVED            : 'page_projects_details_archived',
    DETAILS_DELETE_CLICKED      : 'page_projects_details_delete_clicked',
    DETAILS_DELETED             : 'page_projects_details_deleted',
    APP_SCOPE_LOADED            : 'page_project_app_scope_loaded',
    APP_SCOPE_APP_SELECTED      : 'page_project_app_scope_app_selected',
    APP_SCOPE_SETTING_CHANGED   : 'page_project_app_scope_setting_changed',
    APP_SCOPE_SAVED             : 'page_project_app_scope_saved',
    APP_SCOPE_SAVE_FAILED       : 'page_project_app_scope_save_failed',
    CONFIGS_LOADED              : 'page_project_configs_loaded',
    CONFIG_ASSIGNED             : 'page_project_config_assigned',
    CONFIG_UNASSIGNED           : 'page_project_config_unassigned',
    CONFIG_VERSION_SELECTED     : 'page_project_config_version_selected',
    CONFIGS_SAVED               : 'page_project_configs_saved',

    INGESTION_STATUS_OPENED        : 'page_project_ingestion_status_opened',
    INGESTION_BATCH_SELECTED       : 'page_project_ingestion_batch_selected',
    INGESTION_BATCH_DETAILS_OPENED : 'page_project_ingestion_batch_details_opened',
    INGESTION_RETRY_INITIATED      : 'page_project_ingestion_retry_initiated',
    INGESTION_RETRY_CONFIRMED      : 'page_project_ingestion_retry_confirmed',
    INGESTION_RETRY_EXECUTED       : 'page_project_ingestion_retry_executed',
    INGESTION_RETRY_FAILED         : 'page_project_ingestion_retry_failed',
    INGESTION_LOGS_OPENED          : 'page_project_ingestion_logs_opened',

    ASSISTANT_OPENED                   : 'page_project_assistant_opened',
    ASSISTANT_QUERY_SUBMITTED          : 'page_project_assistant_query_submitted',
    ASSISTANT_RESPONSE_RECEIVED        : 'page_project_assistant_response_received',
    ASSISTANT_RESPONSE_HELPFUL_VOTED   : 'page_project_assistant_response_helpful_voted',
    ASSISTANT_RESPONSE_UNHELPFUL_VOTED : 'page_project_assistant_response_unhelpful_voted',
    ASSISTANT_LINK_CLICKED             : 'page_project_assistant_link_clicked',
    ASSISTANT_CLOSED                   : 'page_project_assistant_closed',

    PUSH_HISTORY_LOADED     : 'page_project_push_history_loaded',
    PUSH_ENTRY_CLICKED      : 'page_project_push_entry_clicked',
    PUSH_DETAILS_OPENED     : 'page_project_push_details_opened',
    PUSH_ROLLBACK_INITIATED : 'page_project_push_rollback_initiated',
    PUSH_ROLLBACK_CONFIRMED : 'page_project_push_rollback_confirmed',
    PUSH_ROLLBACK_EXECUTED  : 'page_project_push_rollback_executed',
    PUSH_ROLLBACK_FAILED    : 'page_project_push_rollback_failed',
  },

  // ── CDG (Customer Data Generator) ──
  CDG : {
    LOADED                       : 'page_cdg_loaded',
    DATA_SOURCE_SELECTED         : 'page_cdg_data_source_selected',
    SAMPLE_ROWS_LOADED           : 'page_cdg_sample_rows_loaded',
    MAPPING_CREATED              : 'page_cdg_mapping_created',
    MAPPING_EDITED               : 'page_cdg_mapping_edited',
    VALIDATION_TRIGGERED         : 'page_cdg_validation_triggered',
    VALIDATION_PASSED            : 'page_cdg_validation_passed',
    VALIDATION_FAILED            : 'page_cdg_validation_failed',
    TEST_EMAIL_CLICKED           : 'page_cdg_test_email_clicked',
    TEST_EMAIL_RECIPIENT_ENTERED : 'page_cdg_test_email_recipient_entered',
    TEST_EMAIL_SUBMITTED         : 'page_cdg_test_email_submitted',
    TEST_EMAIL_SUCCESS           : 'page_cdg_test_email_success',
    TEST_EMAIL_FAILED            : 'page_cdg_test_email_failed',
    DATA_LOADED                  : 'page_cdg_data_loaded',
    DATA_PUSH_INITIATED          : 'page_cdg_data_push_initiated',
    DATA_PUSH_CONFIRMED          : 'page_cdg_data_push_confirmed',
    DATA_PUSH_COMPLETED          : 'page_cdg_data_push_completed',
    DATA_PUSH_FAILED             : 'page_cdg_data_push_failed',
  },

  // ── Workflow (list / editor / node specs / user metadata) ──
  WORKFLOW : {
    LIST_LOADED        : 'page_workflow_list_loaded',
    FILTER_APPLIED     : 'page_workflow_filter_applied',
    SORT_CHANGED       : 'page_workflow_sort_changed',
    SEARCH_EXECUTED    : 'page_workflow_search_executed',
    WORKFLOW_CLICKED   : 'page_workflow_workflow_clicked',
    EDITOR_OPENED      : 'page_workflow_editor_opened',
    EDITOR_MODE_VISUAL : 'page_workflow_editor_mode_visual',
    EDITOR_MODE_CODE   : 'page_workflow_editor_mode_code',
    NODE_ADDED         : 'page_workflow_node_added',
    NODE_CONFIGURED    : 'page_workflow_node_configured',
    NODE_DELETED       : 'page_workflow_node_deleted',
    NODE_DRAGGED       : 'page_workflow_node_dragged',
    CONNECTION_CREATED : 'page_workflow_connection_created',
    CONNECTION_DELETED : 'page_workflow_connection_deleted',
    CONNECTION_LABELED : 'page_workflow_connection_labeled',
    DRAFT_SAVED        : 'page_workflow_draft_saved',
    PUBLISHED          : 'page_workflow_published',
    PUBLISH_FAILED     : 'page_workflow_publish_failed',
    UNDO_CLICKED       : 'page_workflow_undo_clicked',
    REDO_CLICKED       : 'page_workflow_redo_clicked',
    ZOOM_CHANGED       : 'page_workflow_zoom_changed',
    PAN_EXECUTED       : 'page_workflow_pan_executed',
    NODE_SPEC_OPENED            : 'page_workflow_node_spec_opened',
    NODE_SPEC_RULE_ADDED        : 'page_workflow_node_spec_rule_added',
    NODE_SPEC_RULE_REMOVED      : 'page_workflow_node_spec_rule_removed',
    NODE_SPEC_CONDITION_EDITED  : 'page_workflow_node_spec_condition_edited',
    NODE_SPEC_ACTION_CONFIGURED : 'page_workflow_node_spec_action_configured',
    NODE_SPEC_SAVED             : 'page_workflow_node_spec_saved',
    METADATA_PANEL_OPENED  : 'page_workflow_metadata_panel_opened',
    METADATA_FIELD_ADDED   : 'page_workflow_metadata_field_added',
    METADATA_FIELD_REMOVED : 'page_workflow_metadata_field_removed',
    METADATA_FIELD_MAPPED  : 'page_workflow_metadata_field_mapped',
    METADATA_SAVED         : 'page_workflow_metadata_saved',
  },

  // ── CMS (projects / profiles / surveys) ──
  CMS : {
    PROJECTS_LOADED         : 'page_cms_projects_loaded',
    PROJECT_CREATED         : 'page_cms_project_created',
    PROJECT_EDITED          : 'page_cms_project_edited',
    PROJECT_DELETED         : 'page_cms_project_deleted',
    PROJECT_SETTINGS_OPENED : 'page_cms_project_settings_opened',
    PROJECT_SETTINGS_SAVED  : 'page_cms_project_settings_saved',
    PROFILES_LOADED         : 'page_cms_profiles_loaded',
    PROFILE_CREATED         : 'page_cms_profile_created',
    PROFILE_EDITED          : 'page_cms_profile_edited',
    PROFILE_DELETED         : 'page_cms_profile_deleted',
    PROFILE_CLONED          : 'page_cms_profile_cloned',
    PROFILE_ASSIGNED_TO_PROJECT : 'page_cms_profile_assigned_to_project',
    SURVEYS_LOADED          : 'page_cms_surveys_loaded',
    SURVEY_CREATED          : 'page_cms_survey_created',
    SURVEY_EDITED           : 'page_cms_survey_edited',
    SURVEY_DELETED          : 'page_cms_survey_deleted',
    SURVEY_QUESTION_ADDED   : 'page_cms_survey_question_added',
    SURVEY_QUESTION_REMOVED : 'page_cms_survey_question_removed',
    SURVEY_PUBLISHED        : 'page_cms_survey_published',
    SURVEY_UNPUBLISHED      : 'page_cms_survey_unpublished',
    SURVEY_RESPONSES_VIEWED : 'page_cms_survey_responses_viewed',
    SURVEY_RESPONSE_COUNT_LOADED : 'page_cms_survey_response_count_loaded',
  },

  // ── Environment selection ──
  ENV : {
    SELECTOR_OPENED : 'page_env_selector_opened',
    SELECTED        : 'page_env_selected',
    INFO_VIEWED     : 'page_env_info_viewed',
  },

  // ── Admin console (users / roles / org settings / audit log) ──
  ADMIN : {
    CONSOLE_LOADED            : 'page_admin_console_loaded',
    USERS_LIST_LOADED         : 'page_admin_users_list_loaded',
    USER_CREATED              : 'page_admin_user_created',
    USER_EDITED               : 'page_admin_user_edited',
    USER_DISABLED             : 'page_admin_user_disabled',
    USER_DELETED              : 'page_admin_user_deleted',
    USER_PERMISSIONS_CHANGED  : 'page_admin_user_permissions_changed',
    ROLES_LIST_LOADED         : 'page_admin_roles_list_loaded',
    ROLE_CREATED              : 'page_admin_role_created',
    ROLE_EDITED               : 'page_admin_role_edited',
    ROLE_DELETED              : 'page_admin_role_deleted',
    ROLE_PERMISSIONS_MODIFIED : 'page_admin_role_permissions_modified',
    ORG_SETTINGS_OPENED       : 'page_admin_org_settings_opened',
    ORG_SETTING_CHANGED       : 'page_admin_org_setting_changed',
    ORG_SETTINGS_SAVED        : 'page_admin_org_settings_saved',
    AUDIT_LOG_OPENED          : 'page_admin_audit_log_opened',
    AUDIT_LOG_FILTERED        : 'page_admin_audit_log_filtered',
  },

  // ── Navigation (sidebar / breadcrumb / header) ──
  NAV : {
    SIDEBAR_TOGGLED             : 'page_sidebar_toggled',
    BREADCRUMB_CLICKED          : 'page_breadcrumb_clicked',
    HEADER_SEARCH_EXECUTED      : 'page_header_search_executed',
    HEADER_NOTIFICATION_OPENED  : 'page_header_notification_opened',
    HEADER_NOTIFICATION_CLICKED : 'page_header_notification_clicked',
    HEADER_PROFILE_OPENED       : 'page_header_profile_opened',
    HEADER_LOGOUT_CLICKED       : 'page_header_logout_clicked',
  },

  // ── Session & global ──
  SESSION : {
    ERROR_OCCURRED     : 'page_session_error_occurred',
    TIMEOUT_WARNING    : 'page_session_timeout_warning',
    TIMEOUT_CONFIRMED  : 'page_session_timeout_confirmed',
    EXTENDED           : 'page_session_extended',
    APP_CRASH_DETECTED : 'page_app_crash_detected',
    API_ERROR_RECEIVED : 'page_api_error_received',
    NETWORK_OFFLINE    : 'page_network_offline',
    NETWORK_ONLINE     : 'page_network_online',
  },

} as const
